import React, { useState } from 'react';
import { X } from 'lucide-react';
import JobSeekerForm from './JobSeekerForm';
import StripeCheckoutButton from '../../payments/StripeCheckoutButton';
import { STRIPE_PRODUCTS } from '../../../stripe-config';
import type { JobSeekerProfileData } from '../../../types/profiles';

interface CreateProfileModalProps {
  onClose: () => void;
  onSubmit: (data: JobSeekerProfileData) => Promise<string>; // Returns profile ID
}

export default function CreateProfileModal({ onClose, onSubmit }: CreateProfileModalProps) {
  const [step, setStep] = useState<'form' | 'payment'>('form');
  const [profileId, setProfileId] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<JobSeekerProfileData | null>(null);

  const jobSeekerProduct = STRIPE_PRODUCTS.find(p => p.name === 'Job seeker Card');

  const initialData: JobSeekerProfileData = {
    jobTypes: ['full-time'],
    locations: ['Any location'],
    skills: ['General ranch work'],
    experience: 'entry',
    availability: 'immediate',
    desiredPay: '15',
    certifications: [],
    about: '',
    email: '',
    phone: ''
  };

  const handleFormSubmit = async (data: JobSeekerProfileData) => {
    console.log('Form data:', data);
    
    try {
      // Save the profile to database FIRST
      const createdProfileId = await onSubmit(data);
      console.log('Profile saved with ID:', createdProfileId);
      
      // Store the profile ID and data
      setProfileId(createdProfileId);
      setProfileData(data);
      
      // Move to payment step
      setStep('payment');
      console.log('Moved to payment step');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert(`Error saving profile: ${(error as Error).message}`);
    }
  };

  if (!jobSeekerProduct) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
          <p className="text-red-600">Job seeker product not found. Please contact support.</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-300 rounded-md">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="h-6 w-6" />
        </button>

        <h2 className="text-2xl font-semibold mb-6">Create Job Seeker Profile</h2>

        {step === 'form' ? (
          <div>
            {/* Check for pending job application */}
            {(() => {
              const pendingApplication = localStorage.getItem('pendingJobApplication');
              if (pendingApplication) {
                try {
                  const parsed = JSON.parse(pendingApplication);
                  return (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
                      <p className="text-green-800 text-sm">
                        🎯 <strong>Applying for:</strong> {parsed.jobTitle}
                      </p>
                      <p className="text-green-700 text-xs mt-1">
                        Complete your profile to apply for this position
                      </p>
                    </div>
                  );
                } catch (e) {
                  return null;
                }
              }
              return null;
            })()}
            
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
              <h3 className="font-medium text-amber-900 mb-2">Profile Creation Fee</h3>
              <p className="text-amber-800 text-sm">
                Creating a job seeker profile requires a one-time $5.00 payment. This helps maintain quality profiles and supports the platform.
              </p>
            </div>
            
            <JobSeekerForm
              initialData={initialData}
              onSubmit={handleFormSubmit}
              onCancel={onClose}
            />
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
              <h3 className="font-medium text-amber-900 mb-2">Complete Payment Required</h3>
              <p className="text-amber-800 text-sm">
                ✅ Your profile information has been saved! Complete the $5.00 payment to publish your profile and make it visible to employers.
              </p>
            </div>
            
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Complete Profile Creation
            </h3>
            <p className="text-gray-600 mb-6">
              A one-time $5.00 payment is required to publish your job seeker profile and make it visible to potential employers.
            </p>
            <div className="text-2xl font-bold text-gray-900 mb-6">
              ${jobSeekerProduct.price.toFixed(2)}
            </div>
            <div className="space-y-4">
              <StripeCheckoutButton
                priceId={jobSeekerProduct.priceId}
                className="w-full max-w-sm mx-auto"
              >
                Complete Payment & Publish Profile
              </StripeCheckoutButton>
              <div className="space-x-4">
                <button
                  onClick={() => setStep('form')}
                  className="text-sm text-gray-600 hover:text-gray-800 underline"
                >
                  ← Edit profile
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              Secure payment processed by Stripe. Your profile will be published immediately after payment.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}